import { createEvent, createStore, sample } from 'effector'

import { $creatorId } from '@/models/creators.ts'
import { toast } from '@/models/toasts'
import {
  $allowedFanNumbers,
  updateAllowedFanNumbersFx,
} from '@/modules/automations/models/fan-numbering'
import { handleError } from '@/utils/error'

export type NumberAssignmentsState = Record<number, string[]>

export const $numberAssignments = createStore<NumberAssignmentsState>(
  {},
  {
    name: 'number assignments store',
  },
)

export const setNumberAssignments = createEvent<NumberAssignmentsState>({
  name: 'set number assignments',
})
export const updateNumberAssignment = createEvent<{
  numberIndex: number
  memberIds: string[]
}>({
  name: 'update number assignment',
})

export const cleanupNumberAssignments = createEvent<number>({
  name: 'cleanup number assignments',
})

export const handleMemberAssign = createEvent<{
  numberIndex: string
  memberIds: string[]
}>({
  name: 'handle member assign',
})

$numberAssignments
  .on(setNumberAssignments, (_, payload) => payload)
  .on(updateNumberAssignment, (state, { numberIndex, memberIds }) => ({
    ...state,
    [numberIndex]: memberIds,
  }))
  .on(cleanupNumberAssignments, (state, maxNumbers) => {
    const cleanedState: NumberAssignmentsState = {}

    Object.entries(state).forEach(([numberStr, memberIds]) => {
      const numberIndex = Number(numberStr)

      if (numberIndex <= maxNumbers) {
        cleanedState[numberIndex] = memberIds
      }
    })

    return cleanedState
  })

const processAssignment = createEvent<{
  numberAssignmentsState: NumberAssignmentsState
  creatorId: string
  numberIndex: string
  memberIds: string[]
}>({
  name: 'process assignment',
})

processAssignment.watch(
  async ({ numberAssignmentsState, creatorId, numberIndex, memberIds }) => {
    try {
      const memberIdsNumbers = memberIds.map((id) => parseInt(id))
      const fanNumberToUserId: Record<string, number[]> = {}

      Object.entries(numberAssignmentsState || {}).forEach(([num, users]) => {
        if (Array.isArray(users)) {
          fanNumberToUserId[String(num)] = users.map((id) => +id)
        }
      })

      fanNumberToUserId[numberIndex] = memberIdsNumbers

      await updateAllowedFanNumbersFx({
        creatorId: Number(creatorId),
        fanNumberToUserId,
      })

      updateNumberAssignment({ numberIndex: Number(numberIndex), memberIds })
      toast.success(
        'Successfully saved. Changes will be applied within a few minutes',
      )
    } catch (error) {
      console.error('Error updating fan number assignment:', error)
      handleError(error, 'Failed to update fan number assignment')
    }
  },
)

sample({
  clock: handleMemberAssign,
  source: { state: $numberAssignments, creatorId: $creatorId },
  filter: ({ creatorId }) => !!creatorId,
  fn: ({ state, creatorId }, { numberIndex, memberIds }) => ({
    numberAssignmentsState: state,
    creatorId: String(creatorId!),
    numberIndex,
    memberIds,
  }),
  target: processAssignment,
})

sample({
  clock: $allowedFanNumbers,
  filter: Boolean,
  fn: (allowedFanNumbers) => {
    const initialState = Object.entries(
      allowedFanNumbers?.userIdsToTeamMember || {},
    ).reduce(
      (numberAssignments, [fanNumber, teamMembers]) => {
        numberAssignments[Number(fanNumber)] = teamMembers.map((member) =>
          String(member.userId),
        )

        return numberAssignments
      },
      {} as Record<number, string[]>,
    )

    return initialState
  },
  target: setNumberAssignments,
})
